import { Chess, type Square as CjSquare } from 'chess.js';
import type { Color, Square } from '@domain/chess/types';

export interface AttackedPiece {
  sq: Square;
  /** Number of opponent pieces attacking this square. */
  attackerCount: number;
  /** Number of friendly pieces defending this square (if any). */
  defenderCount: number;
  owner: Color;
}

/**
 * Pieces that are currently attacked by at least one enemy unit.
 * King included (in check = attacked). Returns attacker and defender counts for UI badges.
 */
export function attackedPieces(fen: string): AttackedPiece[] {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    return [];
  }

  const out: AttackedPiece[] = [];

  for (const row of game.board()) {
    for (const cell of row) {
      if (!cell) continue;
      const sq = cell.square as Square;
      const owner = cell.color as Color;
      const opp: Color = owner === 'w' ? 'b' : 'w';
      const attackers = game.attackers(sq as CjSquare, opp);
      if (attackers.length === 0) continue;
      const defenders = game.attackers(sq as CjSquare, owner).filter((from) => from !== sq);
      out.push({
        sq,
        attackerCount: attackers.length,
        defenderCount: defenders.length,
        owner,
      });
    }
  }

  return out;
}

/**
 * Pieces on the board that are neither attacked by any enemy unit
 * nor defended by any friendly unit.
 */
export function unattackedUndefendedPieces(fen: string): Square[] {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    return [];
  }

  const out: Square[] = [];

  for (const row of game.board()) {
    for (const cell of row) {
      if (!cell) continue;
      const sq = cell.square as Square;
      const owner = cell.color as Color;
      const opp: Color = owner === 'w' ? 'b' : 'w';
      const attackers = game.attackers(sq as CjSquare, opp);
      if (attackers.length > 0) continue;
      const defenders = game.attackers(sq as CjSquare, owner).filter((from) => from !== sq);
      if (defenders.length > 0) continue;
      out.push(sq);
    }
  }

  return out;
}
